import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format } from "date-fns";
import { formatDistanceToNowStrict } from "date-fns";
import {
  Users,
  BookOpen,
  Calendar,
  CalendarClock,
  ClipboardList,
  Mail,
  Award,
  Eye,
  EyeOff,
  Inbox,
  CheckCircle2,
  XCircle,
  LogIn,
} from "lucide-react";

const sectionTabs = [
  { key: "overview", label: "Overview" },
  { key: "applications", label: "Applications" },
  { key: "counsellors", label: "Counsellors" },
  { key: "stories", label: "Story moderation" },
  { key: "bookings", label: "Bookings" },
  { key: "todos", label: "User to-dos" },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stories, setStories] = useState([]);
  const [applications, setApplications] = useState([]);

  const refresh = async () => {
    try {
      const { data: overview } = await api.get("/admin/overview");
      setData(overview);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load overview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (tab === "stories") {
      api.get("/stories?limit=100").then(({ data }) => setStories(data)).catch(() => {});
    }
    if (tab === "applications") {
      api
        .get("/therapist-applications")
        .then(({ data }) => setApplications(data))
        .catch(() => {});
    }
  }, [tab]);

  const togglePublish = async (story) => {
    const next = story.is_published === false ? true : false;
    try {
      await api.patch(`/admin/stories/${story.id}`, { is_published: next });
      toast.success(next ? "Story republished" : "Story unpublished");
      setStories((prev) => prev.map((s) => (s.id === story.id ? { ...s, is_published: next } : s)));
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update");
    }
  };

  const decideApp = async (app, kind) => {
    try {
      await api.post(`/admin/applications/${app.id}/${kind}`);
      toast.success(kind === "approve" ? "Approved — welcome email sent." : "Politely declined.");
      const { data } = await api.get("/therapist-applications");
      setApplications(data);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-dashboard">
      <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-3">
        <span className="editorial-rule" />Admin Dashboard
      </div>
      <h1 className="font-serif text-4xl text-mindora-ink leading-tight">
        Hello, Jiya.<br />
        <em className="italic text-mindora-red">Everything Mindora, in one quiet view.</em>
      </h1>

      {/* Section tabs */}
      <div className="mt-8 border-b border-mindora-line flex flex-wrap gap-1" data-testid="admin-tabs">
        {sectionTabs.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`text-xs uppercase tracking-[0.2em] px-4 py-3 transition-colors ${
              tab === t.key
                ? "text-mindora-red border-b-2 border-mindora-red -mb-px"
                : "text-mindora-ink/70 hover:text-mindora-red"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-12 text-mindora-muted">Loading…</div>
      ) : !data ? (
        <div className="mt-12 text-mindora-red">Could not load admin data.</div>
      ) : (
        <div className="mt-10">
          {tab === "overview" && <OverviewSection data={data} />}
          {tab === "applications" && (
            <ApplicationsSection apps={applications} onDecide={decideApp} />
          )}
          {tab === "counsellors" && <CounsellorsSection counsellors={data.counsellors} />}
          {tab === "stories" && (
            <StoriesSection stories={stories} onToggle={togglePublish} />
          )}
          {tab === "bookings" && <BookingsSection bookings={data.recent.bookings} />}
          {tab === "todos" && <TodosSection counts={data.counts} />}
        </div>
      )}
    </div>
  );
}

/* ------------ Sections ------------ */

function StatCard({ icon, label, value, sub, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-mindora-line bg-mindora-panel p-5 shadow-clay"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-mindora-muted">
        {icon}
        {label}
      </div>
      <div className="font-serif text-4xl text-mindora-ink mt-2 leading-none">{value}</div>
      {sub && <div className="text-xs text-mindora-muted mt-2">{sub}</div>}
    </div>
  );
}

function OverviewSection({ data }) {
  const c = data.counts;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-overview-stats">
        <StatCard icon={<Users size={12} />} label="Users" value={c.users} testId="stat-users" />
        <StatCard icon={<Award size={12} />} label="Counsellors" value={c.counsellors} sub={c.counsellors_never_signed_in > 0 ? `${c.counsellors_signed_in} signed in · ${c.counsellors_never_signed_in} still to onboard` : `${c.counsellors_signed_in} signed in`} testId="stat-counsellors" />
        <StatCard icon={<Inbox size={12} />} label="Pending applications" value={c.applications_pending} testId="stat-applications" />
        <StatCard
          icon={<BookOpen size={12} />}
          label="Stories"
          value={c.stories_published}
          sub={c.stories_unpublished ? `${c.stories_unpublished} hidden` : "all visible"}
          testId="stat-stories"
        />
        <StatCard icon={<Calendar size={12} />} label="Upcoming bookings" value={c.bookings_upcoming} testId="stat-bookings-upcoming" />
        <StatCard icon={<CalendarClock size={12} />} label="Past bookings" value={c.bookings_past} sub={`${c.bookings_cancelled} cancelled`} testId="stat-bookings-past" />
        <StatCard icon={<Mail size={12} />} label="Letters scheduled" value={c.letters_scheduled} sub={`${c.letters_delivered} delivered`} testId="stat-letters" />
        <StatCard icon={<ClipboardList size={12} />} label="Open to-dos" value={c.todos_open} sub={`${c.todos_done} done`} testId="stat-todos" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
        <ActivityCard title="Latest stories" testId="feed-stories">
          {data.recent.stories.map((s) => (
            <div key={s.id} className="flex items-start justify-between py-2.5 border-b border-mindora-line/60 last:border-0">
              <div className="min-w-0">
                <div className="font-serif text-mindora-ink truncate">{s.title || "Untitled"}</div>
                <div className="text-[11px] text-mindora-muted tracking-wide mt-0.5">
                  {s.author_name} · {s.tag || "—"} · {s.relate_count} relates
                </div>
              </div>
              <div className="text-[10px] text-mindora-muted whitespace-nowrap ml-3">
                {s.created_at ? format(new Date(s.created_at), "MMM d, h:mm a") : "—"}
              </div>
            </div>
          ))}
          {data.recent.stories.length === 0 && <Empty>No stories yet.</Empty>}
        </ActivityCard>

        <ActivityCard title="Latest bookings" testId="feed-bookings">
          {data.recent.bookings.map((b) => (
            <div key={b.id} className="flex items-start justify-between py-2.5 border-b border-mindora-line/60 last:border-0">
              <div className="min-w-0">
                <div className="font-serif text-mindora-ink truncate">{b.user_name} → {b.therapist_name}</div>
                <div className="text-[11px] text-mindora-muted tracking-wide mt-0.5">
                  {b.slot ? format(new Date(b.slot), "EEE MMM d, h:mm a") : "—"} · {b.status}
                </div>
              </div>
            </div>
          ))}
          {data.recent.bookings.length === 0 && <Empty>No bookings yet.</Empty>}
        </ActivityCard>

        <ActivityCard title="Latest applications" testId="feed-applications">
          {data.recent.applications.map((a) => (
            <div key={a.id} className="flex items-start justify-between py-2.5 border-b border-mindora-line/60 last:border-0">
              <div className="min-w-0">
                <div className="font-serif text-mindora-ink truncate">{a.name} <span className="text-xs text-mindora-muted">· {a.title}</span></div>
                <div className="text-[11px] text-mindora-muted tracking-wide mt-0.5">
                  {a.email} · ₹{a.session_price?.toLocaleString("en-IN") || 0} · <em>{a.status}</em>
                </div>
              </div>
            </div>
          ))}
          {data.recent.applications.length === 0 && <Empty>No applications yet.</Empty>}
        </ActivityCard>

        <ActivityCard title="New sign-ups" testId="feed-signups">
          {data.recent.signups.map((u) => (
            <div key={u.id} className="flex items-start justify-between py-2.5 border-b border-mindora-line/60 last:border-0">
              <div className="min-w-0">
                <div className="font-serif text-mindora-ink truncate">{u.name || u.email}</div>
                <div className="text-[11px] text-mindora-muted tracking-wide mt-0.5">
                  {u.email} · {u.role}
                </div>
              </div>
              <div className="text-[10px] text-mindora-muted whitespace-nowrap ml-3">
                {u.created_at ? format(new Date(u.created_at), "MMM d") : "—"}
              </div>
            </div>
          ))}
        </ActivityCard>
      </div>
    </>
  );
}

function CounsellorsSection({ counsellors }) {
  return (
    <div className="rounded-2xl border border-mindora-line overflow-hidden" data-testid="admin-counsellors-table">
      <table className="w-full text-sm">
        <thead className="bg-mindora-panel text-mindora-muted">
          <tr className="text-left text-[11px] uppercase tracking-[0.18em]">
            <th className="px-5 py-3">Counsellor</th>
            <th className="px-5 py-3">Experience</th>
            <th className="px-5 py-3">Price</th>
            <th className="px-5 py-3">Last login</th>
            <th className="px-5 py-3 text-right">Upcoming</th>
            <th className="px-5 py-3 text-right">Past</th>
            <th className="px-5 py-3 text-right">Offered</th>
            <th className="px-5 py-3 text-right">Rating</th>
          </tr>
        </thead>
        <tbody>
          {counsellors.map((c) => (
            <tr key={c.id} className="border-t border-mindora-line/60 hover:bg-mindora-panel/40">
              <td className="px-5 py-3">
                <Link to={`/directory/${c.id}`} className="font-serif text-mindora-ink hover:text-mindora-red">
                  {c.name}
                </Link>
                <div className="text-[11px] text-mindora-muted">{c.title}</div>
                {c.login_email && (
                  <div className="text-[10px] text-mindora-muted/80 mt-0.5 tracking-wide">{c.login_email}</div>
                )}
              </td>
              <td className="px-5 py-3 text-mindora-ink/80">{c.experience || "—"}</td>
              <td className="px-5 py-3 text-mindora-ink/80">{c.session_price_display}</td>
              <td className="px-5 py-3" data-testid={`counsellor-${c.id}-login-status`}>
                <LoginStatusPill lastLoginAt={c.last_login_at} />
              </td>
              <td className="px-5 py-3 text-right font-serif text-mindora-ink">{c.upcoming_sessions}</td>
              <td className="px-5 py-3 text-right text-mindora-ink/70">{c.past_sessions}</td>
              <td className="px-5 py-3 text-right text-mindora-ink/70">{c.offered_slots}</td>
              <td className="px-5 py-3 text-right text-mindora-red">{c.rating}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LoginStatusPill({ lastLoginAt }) {
  if (!lastLoginAt) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] rounded-full bg-mindora-red text-mindora-bg px-2.5 py-0.5">
        <LogIn size={10} /> Never signed in
      </span>
    );
  }
  const d = new Date(lastLoginAt);
  const daysAgo = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
  const isDormant = daysAgo >= 14;
  return (
    <div>
      <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] rounded-full px-2.5 py-0.5 ${isDormant ? "bg-mindora-line text-mindora-ink/70" : "bg-mindora-ink/90 text-mindora-bg"}`}>
        <LogIn size={10} /> {isDormant ? "Dormant" : "Active"}
      </span>
      <div className="text-[10px] text-mindora-muted mt-1">
        {formatDistanceToNowStrict(d, { addSuffix: true })}
      </div>
    </div>
  );
}

function StoriesSection({ stories, onToggle }) {
  const grouped = useMemo(
    () => ({
      published: stories.filter((s) => s.is_published !== false),
      hidden: stories.filter((s) => s.is_published === false),
    }),
    [stories]
  );
  return (
    <div className="space-y-8" data-testid="admin-stories-section">
      <StoryGroup title={`Published (${grouped.published.length})`} items={grouped.published} onToggle={onToggle} variant="published" />
      <StoryGroup title={`Hidden (${grouped.hidden.length})`} items={grouped.hidden} onToggle={onToggle} variant="hidden" />
    </div>
  );
}

function StoryGroup({ title, items, onToggle, variant }) {
  return (
    <div data-testid={`admin-stories-${variant}-wrap`}>
      <h3 className="text-xs uppercase tracking-[0.2em] text-mindora-ink/80 mb-3">{title}</h3>
      {items.length === 0 ? (
        <div data-testid={`admin-stories-${variant}`}>
          <Empty>{variant === "hidden" ? "Nothing hidden right now." : "No stories yet."}</Empty>
        </div>
      ) : (
        <ul className="space-y-2" data-testid={`admin-stories-${variant}`}>
          {items.map((s) => (
            <li
              key={s.id}
              className="flex items-center justify-between rounded-xl border border-mindora-line bg-mindora-panel px-4 py-3"
              data-testid={`admin-story-${s.id}`}
            >
              <div className="min-w-0">
                <Link to={`/feed/${s.id}`} className="font-serif text-mindora-ink hover:text-mindora-red truncate block">
                  {s.title}
                </Link>
                <div className="text-[11px] text-mindora-muted">
                  {s.author_name} · {s.tag} · {s.relate_count} relates
                </div>
              </div>
              <Button
                data-testid={`admin-story-${s.id}-toggle`}
                variant="outline"
                onClick={() => onToggle(s)}
                className="rounded-full border-mindora-line text-xs"
              >
                {variant === "hidden" ? (
                  <>
                    <Eye size={13} className="mr-1.5" /> Republish
                  </>
                ) : (
                  <>
                    <EyeOff size={13} className="mr-1.5" /> Unpublish
                  </>
                )}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ApplicationsSection({ apps, onDecide }) {
  const pending = apps.filter((a) => a.status === "received" || a.status === "pending");
  const decided = apps.filter((a) => a.status === "approved" || a.status === "rejected");
  return (
    <div className="space-y-8" data-testid="admin-applications-section">
      <div>
        <h3 className="text-xs uppercase tracking-[0.2em] text-mindora-ink/80 mb-3">Pending ({pending.length})</h3>
        {pending.length === 0 ? (
          <Empty>No pending applications.</Empty>
        ) : (
          pending.map((a) => (
            <div
              key={a.id}
              className="rounded-2xl border border-mindora-line bg-mindora-panel p-5 mb-3"
              data-testid={`admin-app-${a.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="font-serif text-2xl text-mindora-ink">{a.name}</div>
                  <div className="text-xs text-mindora-muted">
                    {a.title} · {a.years_experience} yrs · ₹{(a.session_price || 0).toLocaleString("en-IN")}
                  </div>
                  <div className="text-xs text-mindora-muted mt-1">{a.email}</div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => onDecide(a, "approve")}
                    data-testid={`admin-app-${a.id}-approve`}
                    className="rounded-full btn-primary"
                  >
                    <CheckCircle2 size={14} className="mr-1.5" /> Approve
                  </Button>
                  <Button
                    onClick={() => onDecide(a, "reject")}
                    data-testid={`admin-app-${a.id}-reject`}
                    variant="outline"
                    className="rounded-full border-mindora-line"
                  >
                    <XCircle size={14} className="mr-1.5" /> Decline
                  </Button>
                </div>
              </div>
              {a.specialties?.length > 0 && (
                <div className="text-xs text-mindora-muted mt-3">Specialties: {a.specialties.join(", ")}</div>
              )}
              {a.bio && <p className="text-sm text-mindora-ink/80 mt-3 line-clamp-4 leading-relaxed">{a.bio}</p>}
            </div>
          ))
        )}
      </div>
      {decided.length > 0 && (
        <div>
          <h3 className="text-xs uppercase tracking-[0.2em] text-mindora-ink/80 mb-3">Decided ({decided.length})</h3>
          <ul className="text-sm space-y-1.5">
            {decided.map((a) => (
              <li key={a.id} className="text-mindora-muted">
                <span className="text-mindora-ink">{a.name}</span> · {a.email} ·{" "}
                <em className={a.status === "approved" ? "text-mindora-red" : ""}>{a.status}</em>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function BookingsSection({ bookings }) {
  return (
    <div className="rounded-2xl border border-mindora-line bg-mindora-panel p-5" data-testid="admin-bookings">
      {bookings.length === 0 ? (
        <Empty>No bookings yet.</Empty>
      ) : (
        bookings.map((b) => (
          <div key={b.id} className="flex items-center justify-between py-2.5 border-b border-mindora-line/60 last:border-0">
            <div>
              <div className="font-serif text-mindora-ink">
                {b.user_name} → {b.therapist_name}
              </div>
              <div className="text-xs text-mindora-muted">
                {b.slot ? format(new Date(b.slot), "EEE MMM d, h:mm a") : "—"} · {b.status}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function TodosSection({ counts }) {
  return (
    <div className="grid grid-cols-2 gap-4 max-w-md" data-testid="admin-todos">
      <StatCard icon={<ClipboardList size={12} />} label="Open" value={counts.todos_open} />
      <StatCard icon={<CheckCircle2 size={12} />} label="Completed" value={counts.todos_done} />
    </div>
  );
}

function ActivityCard({ title, children, testId }) {
  return (
    <div className="rounded-2xl border border-mindora-line bg-mindora-bg p-5" data-testid={testId}>
      <h3 className="text-xs uppercase tracking-[0.2em] text-mindora-ink/80 mb-3">{title}</h3>
      <div>{children}</div>
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-sm text-mindora-muted italic py-4">{children}</div>;
}
